import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Key } from "lucide-react";
import { toast } from "sonner";
import { fetchLibrary, recover as recoverExpert } from "@/lib/api";
import { recoveryKey, isAbandoned } from "@/lib/jobs";
import { engineLabel, useTitle } from "@/lib/brand";
import { Loading, PageHead } from "@/components/Kit";

/**
 * MY LIBRARY — what this browser owns, from the hub (backlog 164), and the recover box that
 * binds an Expert to this browser with its seven words (always visible since 09-13, TODO 91).
 * Rebuilt 2026-10-27; unpaid checkouts older than two hours are left out.
 */
export default function LibraryPage() {
  useTitle("My library");
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [owned, setOwned] = useState(undefined);   // undefined = loading, null = unreachable
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchLibrary().then((d) => setOwned((d.items || []).filter((j) => !isAbandoned(j)))).catch(() => setOwned(null));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (hash === "#recover") document.getElementById("recover")?.scrollIntoView({ behavior: "smooth" }); }, [hash, owned]);

  const recover = async () => {
    if (!phrase.trim() || busy) return;
    setBusy(true);
    try {
      const r = await recoverExpert(phrase.trim());
      toast.success("Recovered — it's yours on this browser now.");
      setPhrase("");
      await load();
      if (r.job_id) navigate(`/progress/${r.job_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Those words didn't match an Expert.");
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="library-page" className="ex-page">
      <div className="ex-narrow pb-28">
        <PageHead eyebrow="My library" title="Your Experts." lede="No account and no sign-in. This browser remembers what it owns, and the recovery words of anything it trained." />

        {owned === undefined && <Loading label="Loading your library" />}
        {owned === null && <div className="ex-panel p-8 text-white">We couldn't reach your library just now. Please try again in a moment.</div>}
        {owned && owned.length === 0 && (
          <div className="ex-panel p-10 text-center">
            <div className="text-white">Nothing here yet.</div>
            <div className="ex-small mt-2">Experts you train from this browser appear here.</div>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link to="/train" className="ex-btn ex-btn-primary ex-btn-sm">Train an Expert</Link>
              <Link to="/experts" className="ex-btn ex-btn-secondary ex-btn-sm">Browse free Experts</Link>
            </div>
          </div>
        )}
        {owned && owned.length > 0 && (
          <ul className="space-y-3" data-testid="owned-list">
            {owned.map((j) => (
              <li key={j.job_id}>
                <Link to={`/progress/${j.job_id}`} className="ex-card ex-card-link flex items-center gap-4 p-5" data-testid={`owned-${j.job_id}`}>
                  <span className={`ex-dot flex-none ${j.ready ? "" : "ex-dot-live"}`} style={{ background: j.ready ? "var(--ex-ok)" : undefined }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-white truncate">{j.subject}</span>
                    <span className="block ex-small truncate">
                      {[j.ready ? "Ready to download" : j.progress, j.engine_id && engineLabel(j.engine_id), j.size_bytes ? `${Math.round(j.size_bytes / 1048576)} MB` : null].filter(Boolean).join(" · ")}
                    </span>
                    {recoveryKey(j.job_id) && <span className="block ex-tiny mt-1 truncate" data-testid={`key-${j.job_id}`}>Recovery words: {recoveryKey(j.job_id)}</span>}
                  </span>
                  <span className="ex-btn ex-btn-sm ex-btn-secondary flex-none">{j.ready ? "Download" : "View"} <ArrowRight className="h-4 w-4" strokeWidth={1.8} /></span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <section id="recover" className="mt-16 scroll-mt-24" data-testid="recover-form">
          <div className="ex-panel p-7">
            <div className="flex items-center gap-3">
              <Key className="h-5 w-5 text-white" strokeWidth={1.7} />
              <h2 className="ex-h3">Recover an Expert</h2>
            </div>
            <p className="ex-small mt-3">Trained on another device, or cleared this browser? Enter the seven recovery words to bring the Expert back here.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <input value={phrase} onChange={(e) => setPhrase(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") recover(); }}
                placeholder="seven words, in order" className="ex-text-input" data-testid="recover-phrase-input" autoComplete="off" spellCheck={false} />
              <button type="button" onClick={recover} disabled={busy || !phrase.trim()} className="ex-btn ex-btn-primary flex-none" data-testid="recover-submit-btn">
                {busy ? <span className="ex-spinner" style={{ borderTopColor: "#0b0b0c" }} /> : "Recover"}
              </button>
            </div>
            <p className="ex-tiny mt-3">Capitals, dashes and commas don't matter. After five wrong tries in an hour, it waits before trying again.</p>
          </div>
        </section>
      </div>
    </main>
  );
}
